import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';

import { FileDeletionsApiService } from '@core/data/api/file-deletions-api.service';
import { pendingDeletionFromDto, recoveredFileFromDto } from '@core/data/api/file-deletions.mapper';

import { FileDeletionsApiActions, FileDeletionsPageActions } from './file-deletions.actions';

/**
 * The file-deletion queue.
 *
 * There is no seeded fallback: this screen only means anything against a real
 * server, and inventing a queue of pretend deletions would be inventing files
 * that were never lost.
 */
@Injectable()
export class FileDeletionsEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(FileDeletionsApiService);

  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileDeletionsPageActions.entered),
      switchMap(() => {
        if (!this.api.available()) {
          return of(FileDeletionsApiActions.loadSuccess({ pending: [], recovered: [] }));
        }
        return forkJoin({
          pending: this.api.listPending(),
          recovered: this.api.listRecovered(),
        }).pipe(
          map(({ pending, recovered }) =>
            FileDeletionsApiActions.loadSuccess({
              pending: pending.map(pendingDeletionFromDto),
              recovered: recovered.map(recoveredFileFromDto),
            }),
          ),
          catchError((err: unknown) =>
            of(FileDeletionsApiActions.loadFailure({ error: toMessage(err) })),
          ),
        );
      }),
    ),
  );

  readonly rescue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileDeletionsPageActions.rescueRequested),
      switchMap(({ attachmentGuid, notes }) =>
        this.api.rescue(attachmentGuid, notes).pipe(
          map((dto) =>
            FileDeletionsApiActions.rescued({
              attachmentGuid,
              recovered: recoveredFileFromDto(dto),
            }),
          ),
          catchError((err: unknown) =>
            of(FileDeletionsApiActions.rescueFailed({ attachmentGuid, error: rescueMessage(err) })),
          ),
        ),
      ),
    ),
  );
}

/**
 * Both refusals mean something specific, and saying it beats repeating a status
 * code at somebody who is trying to save a file.
 */
function rescueMessage(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    if (err.status === 404) {
      return 'Too late — the nightly clear-out destroyed this one while the page was open.';
    }
    if (err.status === 409) {
      return 'Already kept, by another administrator working the queue at the same time.';
    }
  }
  return toMessage(err);
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
